<template>
  <div class="asc-advanced-custom-alerts-expression-wrapper">
    <div class="expression-input">
      <div class="asc-pdf-viewer-link" style="margin: 10px 0 10px 0">
        <p class="d-flex align-center">
          Reference the
          <a :href="pdfBlobUrl" :download="guideFilename">
            Logic Builder Guide
            <v-icon center :size="13"> {{ 'mdi-open-in-new' }} </v-icon>
          </a>
          to get started writing custom logic.
          <asc-tooltip
            :tooltip="getTooltip"
            top
            :tooltip-length="100"
            is-icon-tooltip
          >
            <v-icon class="icon-info ml-1" :size="13" style="color: #479d9a">
              mdi-information
            </v-icon>
          </asc-tooltip>
        </p>
      </div>
      <div
        v-if="showLogicValidationErrorMessage"
        class="logic-string-validation-error"
        data-testid="logicValidationErrorMessage"
      >
        <asc-validation-error
          :value="logicStringValidationErrorText"
          :buttons-enabled="false"
        >
        </asc-validation-error>
      </div>
      <div>
        <div style="margin-bottom: 10px">
          <asc-header uppercase size="h3" value="configurations"> </asc-header>
        </div>
        <v-container fluid class="configuration-container">
          <asc-tooltip
            :tooltip-length="100"
            :tooltip="isDataBricksRunning ? tooltipMsg : ''"
            :right="true"
          >
            <v-row class="mb-2">
              <v-col
                cols="12"
                :md="alertType === 'advanced-fullflight' ? 6 : 12"
              >
                <asc-autocomplete-selector
                  id="aircraft"
                  v-model="tailIds"
                  class="aircraftSelector"
                  placeholder="Select an Aircraft"
                  label="Aircraft"
                  use-array
                  component-type="multiple"
                  :disabled="isDataBricksRunning ? true : false"
                  :items="getTails"
                  :readonly="isReadOnly"
                  data-testid="aircraft"
                />
              </v-col>
              <v-col
                v-if="alertType === 'advanced-fullflight'"
                cols="12"
                md="6"
              >
                <asc-autocomplete-selector
                  id="partNumber"
                  v-model="partNumbersSelected"
                  class="partnumberSelector"
                  placeholder="Select a PartNumber"
                  label="Part Number"
                  use-array
                  component-type="multiple"
                  :items="getPartNumbers"
                  :disable-select-all="true"
                  :no-reset="false"
                  :disabled="isDataBricksRunning ? true : false"
                  :readonly="isReadOnly"
                  data-testid="partNumber"
                />
              </v-col>
            </v-row>
          </asc-tooltip>
          <v-row v-if="alertType === 'advanced-acmf'" dense>
            <v-col>
              <label>Persistence</label>
              <asc-tooltip
                :tooltip-length="100"
                :tooltip="isDataBricksRunning ? tooltipMsg : ''"
                :left="true"
              >
                <v-radio-group
                  v-model="configurationType"
                  row
                  dense
                  hide-details
                  :disabled="isDataBricksRunning ? true : false || isReadOnly"
                  class="mt-1"
                  data-testid="configurationType"
                >
                  <v-radio
                    v-for="(option, index) in configurationOptions"
                    :key="index"
                    :value="option.value"
                    style="font-size: 0.8rem"
                    class="small-radio"
                    :ripple="false"
                  >
                    <template #label>
                      <label style="font-size: 14px">{{ option.label }}</label>
                    </template>
                  </v-radio>
                </v-radio-group>
              </asc-tooltip>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col v-if="configurationType === 'aggregateDays'">
              <label>Aggregate</label>
              <div class="d-flex align-center" style="height: 32px">
                <span>
                  Set trigger logic using aggregate functions across
                </span>
                <asc-tooltip
                  :tooltip-length="100"
                  :tooltip="isDataBricksRunning ? tooltipMsg : ''"
                  :left="true"
                >
                  <asc-text-field-input
                    v-model="aggregateDays"
                    type="number"
                    :disabled="isDataBricksRunning ? true : false"
                    :readonly="isReadOnly"
                    class="inline-input"
                    data-testid="aggregateDays"
                  />
                </asc-tooltip>
                <span class="mr-2"> day(s).</span>
                <span v-if="!persistenceInvalid">
                  {{ getAggregateDaysMessage }}
                </span>
                <asc-validation-message
                  message="Please enter a valid unit value 1-30"
                  :show="persistenceInvalid"
                />
              </div>
            </v-col>

            <v-col v-if="configurationType === 'aggregateReports'">
              <label>Aggregate</label>
              <div class="d-flex align-center">
                <span>
                  Set trigger logic using aggregate functions across
                </span>
                <asc-tooltip
                  :tooltip-length="100"
                  :tooltip="isDataBricksRunning ? tooltipMsg : ''"
                  :left="true"
                >
                  <asc-text-field-input
                    v-model="aggregateReports"
                    type="number"
                    :disabled="isDataBricksRunning ? true : false"
                    :readonly="isReadOnly"
                    class="inline-input"
                    data-testid="aggregateReports"
                  ></asc-text-field-input>
                </asc-tooltip>
                <span> report(s).</span>
                <span v-if="!persistenceInvalid">
                  {{ getAggregateReportsMessage }}
                </span>
                <asc-validation-message
                  v-if="persistenceInvalid"
                  message="Please enter a valid unit value 1-60"
                  :show="true"
                />
              </div>
            </v-col>

            <v-col v-if="configurationType === 'persistence'">
              <v-row dense>
                <v-col cols="12" md="1">
                  <asc-tooltip
                    :tooltip-length="100"
                    :tooltip="isDataBricksRunning ? tooltipMsg : ''"
                    left
                  >
                    <asc-autocomplete-selector
                      v-model="persistenceFrom"
                      no-array
                      label="Frequency"
                      :items="getFrequencyItems"
                      :disabled="isDataBricksRunning ? true : false"
                      :readonly="isReadOnly"
                      data-testid="persistenceFrom"
                    />
                  </asc-tooltip>
                </v-col>
                <v-col
                  class="d-flex align-center justify-center px-2"
                  md="auto"
                >
                  of
                </v-col>
                <v-col cols="12" md="1">
                  <asc-tooltip
                    :tooltip-length="100"
                    :tooltip="isDataBricksRunning ? tooltipMsg : ''"
                    left
                  >
                    <asc-autocomplete-selector
                      v-model="persistenceTo"
                      no-array
                      :label="` `"
                      :items="getFrequencyItems"
                      :disabled="isDataBricksRunning ? true : false"
                      :readonly="isReadOnly"
                      style="min-width: 100%; width: 100%"
                      data-testid="persistenceTo"
                    />
                  </asc-tooltip>
                </v-col>
                <v-col
                  class="d-flex align-center justify-center px-2"
                  md="auto"
                >
                  Flights
                </v-col>

                <v-col
                  v-if="persistenceInvalid"
                  class="d-flex align-center justify-center px-2"
                  data-testid="persistenceInvalidMessage"
                >
                  <asc-validation-message
                    message="Please enter
                          valid frequency logic, e.g. 1 of 2: Your analytic will trigger if parameter threshold is met on 1 out of past 2 flights."
                    :show="true"
                  />
                </v-col>
                <v-col
                  v-else
                  class="d-flex align-center justify-start px-2"
                  cols="12"
                  md="8"
                  data-testid="frequencyMessage"
                >
                  {{ getFrequencyMessage }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <asc-query-builder
                is-custom-analytic
                label="Logic string"
                data-testid="query-builder"
                :expression-options="expressionOptions"
                :configuration-option="configurationOption"
                :is-advanced-full-flight="isAdvancedFullFlight"
                :allowed-characters="allowedCharacters"
                :is-advanced-dplf="isAdvancedDplf"
                :message-codes="messageCodes"
                :expression-value="expressionInput"
                :parameter-list="parameterList"
                :flight-phase-list="flightPhaseList"
                :advanced-function-list="advancedFunctionList"
                :is-valid-parameters="isValidParameters"
                :validate-loading="validateLoading"
                :enable-validate-button="enableValidateButton"
                :is-data-bricks-running="isDataBricksRunning"
                :tooltip-msg="tooltipMsg"
                :is-read-only="isReadOnly"
                @input="handleUpdatedText"
                @handleValidate="handleValidate"
                @isDirty="handleIsDirty"
                @onValidateParameters="handleParameterValidator"
              />
            </v-col>
          </v-row>
          <v-row
            v-if="
              isAdvancedFullFlight &&
              checkTier2OrAbove &&
              displayRunPerformanceSection
            "
          >
            <v-col>
              <asc-performance-estimate-wrapper
                :alert-data="alertData"
                :logic-string-valid="logicStringValid"
                :is-look-back-valid="isLookBackValid"
                :is-data-bricks-running="isDataBricksRunning"
                :enable-run-performance-estimate="enableRunPerformanceEstimate"
                :estimate-button-name="estimateButtonName"
                :look-back-response="lookBackResponse"
                :is-read-only="isReadOnly"
                :run-limit-before-first-save="runLimit"
                @onPerformanceAnanlysis="handlePerformanceAnanlysis"
                @onCancelPerformanceAnalysis="handleCancelPerformanceAnalysis"
                @onSeeEstimateDash="handleSeeEstimateDash"
                @onDatabricksErrorNotificationClose="
                  handleDatabricksErrorNotificationClose
                "
              />
            </v-col>
          </v-row>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script>
import AscQueryBuilder from '@/components/atoms/AscQueryBuilder'
import { CUSTOM_ALERT_CONSTS as CAC } from '@/stores/consts'
import AscValidationError from '../atoms/AscValidationError.vue'
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscHeader from '@/components/atoms/AscHeader'
import AscValidationMessage from '@/components/atoms/AscValidationMessage'
import AscTooltip from '@/components/atoms/AscTooltip'
import {
  FULL_FLIGHT_TIER_2,
  COLLINS_PHM_TIER_3,
  COLLINS_PHM_TIER_4,
} from '@/utilities/appConsts'

import AscPerformanceEstimateWrapper from './AscPerformanceEstimateWrapper.vue'
import AscTextFieldInput from '../atoms/AscTextFieldInput.vue'
import {
  ADVANCED_ACMF_CONFIGURATION_OPTIONS,
  ADVANCED_ACMF_AGGREGATE_DAYS_MIN,
  ADVANCED_ACMF_AGGREGATE_DAYS_MAX,
  ADVANCED_ACMF_AGGREGATE_REPORTS_MIN,
  ADVANCED_ACMF_AGGREGATE_REPORTS_MAX,
} from '@/utilities/appConsts'

export default {
  name: 'AscAdvancedCustomAlertsExpressionInputWrapper',

  components: {
    AscQueryBuilder,
    AscAutocompleteSelector,
    AscHeader,
    AscValidationMessage,
    AscPerformanceEstimateWrapper,
    AscTextFieldInput,
    AscTooltip,
    AscValidationError,
  },
  props: {
    displayRunPerformanceSection: {
      type: Boolean,
      default: false,
    },
    tooltipMsg: {
      type: String,
      default: '',
    },
    lookBackResponse: {
      type: Object,
      default: () => {},
    },
    estimateButtonName: {
      type: String,
      default: '',
    },
    enableValidateButton: {
      type: Boolean,
      default: true,
    },
    enableRunPerformanceEstimate: {
      type: Boolean,
      default: false,
    },
    isDataBricksRunning: {
      type: Boolean,
      default: false,
    },
    alertType: {
      type: String,
      default: '',
    },
    alertData: {
      type: Object,
      default: () => {},
    },
    parameterTailList: {
      type: Array,
      default: () => [],
    },
    partNumbers: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    content: {
      type: Object,
      default: () => {},
    },
    user: {
      type: Object,
      default: () => {},
    },
    max: {
      type: Number,
      default: null,
    },
    pdfDocument: {
      type: Blob,
      default: () => new Blob(),
    },
    logicStringValid: {
      type: Boolean,
      default: true,
    },
    validateLoading: {
      type: Boolean,
      default: false,
    },
    expressionInput: {
      type: String,
      default: '',
    },
    allowedCharacters: {
      type: Object,
      default: () => {},
    },
    flightPhaseList: {
      type: Array,
      default: () => [],
    },
    messageCodes: {
      type: Array,
      default: () => [],
    },
    parameterList: {
      type: Array,
      default: () => [],
    },
    advancedFunctionList: {
      type: Array,
      default: () => [],
    },
    expressionOptions: {
      type: Array,
      default: () => [],
    },
    configurationOption: {
      type: String,
      default: '',
    },
    isAdvancedFullFlight: {
      type: Boolean,
      default: false,
    },
    isAdvancedDplf: {
      type: Boolean,
      default: false,
    },
    showLogicValidationErrorMessage: {
      type: Boolean,
      default: false,
    },
    logicStringValidationErrorText: {
      type: String,
      default: '',
    },
    airlinesTierType: {
      type: Number,
      default: 0,
    },
    isLookBackValid: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    runLimit: {
      type: Number,
      default: 0,
    },
  },
  data: function () {
    return {
      persistenceInvalid: false,
      configurationOptions: ADVANCED_ACMF_CONFIGURATION_OPTIONS,
      ADVANCED_ACMF_AGGREGATE_DAYS_MIN,
      ADVANCED_ACMF_AGGREGATE_DAYS_MAX,
      ADVANCED_ACMF_AGGREGATE_REPORTS_MIN,
      ADVANCED_ACMF_AGGREGATE_REPORTS_MAX,
      doReset: true,
      updatedEditorContent: this.expressionInput,
      logicText: '',
      cursorPosition: 0,
      isValidParameters: false,
      isDisabled: true,
      parameterCountText:
        'Up to ' +
        CAC.ADVANCED_CUSTOM_ALERT_PARAMETER_LIMIT +
        ' parameters accepted.',
    }
  },

  computed: {
    getTooltip() {
      return this.alertType === 'advanced-acmf' ||
        this.alertType === 'advanced-fullflight'
        ? 'Logic is restricted to up to 16 parameters.'
        : 'Logic is restricted to up to 4 message codes.'
    },
    tailIds: {
      get() {
        return this.alertData?.tails
      },
      set(val) {
        this.$emit('handleChangeTails', val)
      },
    },
    partNumbersSelected: {
      get() {
        return this.alertData?.partNumbers
      },
      set(val) {
        this.$emit('handleChangePartNumbers', val)
      },
    },
    persistenceFrom: {
      get() {
        return this.alertData?.persistenceFrom
      },
      set(val) {
        this.$emit('isDirty')
        if (val > this.persistenceTo) {
          this.persistenceInvalid = true
        } else {
          this.persistenceInvalid = false
        }
        this.$emit('handleChangePersistenceFrom', val)
        if (this.persistenceInvalid) {
          this.$emit('invalidPersistence')
        } else {
          this.$emit('validPersistence')
        }
      },
    },
    persistenceTo: {
      get() {
        return this.alertData?.persistenceTo
      },
      set(val) {
        this.$emit('isDirty')
        if (val < this.persistenceFrom) {
          this.persistenceInvalid = true
        } else {
          this.persistenceInvalid = false
        }
        this.$emit('handleChangePersistenceTo', val)
        if (this.persistenceInvalid) {
          this.$emit('invalidPersistence')
        } else {
          this.$emit('validPersistence')
        }
      },
    },
    getTails() {
      return this.parameterTailList
    },
    getPartNumbers() {
      return this.partNumbers
    },
    getFrequencyMessage() {
      if (this.persistenceFrom === 1 && this.persistenceTo === 1) {
        return 'Your analytic will trigger if a parameter threshold is met on a single flight.'
      } else {
        return `Your analytic will trigger if a parameter threshold is met on ${this.persistenceFrom} out of ${this.persistenceTo} flights.`
      }
    },
    getFrequencyItems() {
      const numberOfFlights = 10
      const itemArray = []
      for (let i = 1; i <= numberOfFlights; i++) {
        itemArray.push({
          text: i < 10 ? `  ${i}` : `${i}`,
          value: i,
        })
      }
      return itemArray
    },
    isEditable() {
      return true
    },
    getAggregateDaysMessage() {
      return `Limit of ${this.ADVANCED_ACMF_AGGREGATE_DAYS_MAX} days`
    },
    getAggregateReportsMessage() {
      return `Limit of ${this.ADVANCED_ACMF_AGGREGATE_REPORTS_MAX} reports`
    },
    aggregateDays: {
      get() {
        return this.alertData?.aggregateDays
      },
      set(val) {
        this.$emit('isDirty')
        val = Number(val)
        if (isNaN(val)) {
          val = 0
        }
        if (
          !Number.isInteger(val) ||
          val < ADVANCED_ACMF_AGGREGATE_DAYS_MIN ||
          val > ADVANCED_ACMF_AGGREGATE_DAYS_MAX
        ) {
          this.persistenceInvalid = true
        } else {
          this.persistenceInvalid = false
        }
        this.$emit('handleChangeAggregateDays', val)
        if (this.persistenceInvalid) {
          this.$emit('invalidPersistence')
        } else {
          this.$emit('validPersistence')
        }
      },
    },
    aggregateReports: {
      get() {
        return this.alertData?.aggregateReports
      },
      set(val) {
        this.$emit('isDirty')
        val = Number(val)
        if (isNaN(val)) {
          val = 0
        }
        if (
          !Number.isInteger(val) ||
          val < ADVANCED_ACMF_AGGREGATE_REPORTS_MIN ||
          val > ADVANCED_ACMF_AGGREGATE_REPORTS_MAX
        ) {
          this.persistenceInvalid = true
        } else {
          this.persistenceInvalid = false
        }
        this.$emit('handleChangeAggregateReports', val)
        if (this.persistenceInvalid) {
          this.$emit('invalidPersistence')
        } else {
          this.$emit('validPersistence')
        }
      },
    },
    configurationType: {
      get() {
        return this.alertData?.configurationType
      },
      set(val) {
        this.$emit('isDirty')
        this.$emit('handleChangeConfigurationType', val)
      },
    },
    isGuideReady() {
      if (this.pdfDocument === '') return false
      else return true
    },
    getValidateButtonText() {
      if (!this.logicStringValid) {
        return 'Validate'
      } else {
        return ''
      }
    },
    getValidateButtonIcon() {
      if (this.logicStringValid) {
        return 'mdi-check-bold'
      } else {
        return ''
      }
    },
    getValidateButtonIconOnly() {
      return this.logicStringValid
    },
    checkTier2OrAbove() {
      return (
        this.airlinesTierType === FULL_FLIGHT_TIER_2 ||
        COLLINS_PHM_TIER_3 ||
        COLLINS_PHM_TIER_4
      )
    },
    guideFilename() {
      if (this.alertType == 'advanced-fullflight') {
        return 'FullFlightLogicBuilderGuide.pdf'
      } else if (this.alertType == 'advanced-acmf') {
        return 'ACMFLogicBuilderGuide.pdf'
      } else {
        return 'DPLFLogicBuilderGuide.pdf'
      }
    },
    pdfBlobUrl() {
      if (!this.pdfDocument) return ''

      const blob = new Blob([this.pdfDocument], { type: 'application/pdf' })
      return URL.createObjectURL(blob)
    },
  },

  watch: {
    expressionInput: function (value) {
      this.doReset = false
      this.updatedEditorContent = value
    },
    alertData: {
      deep: true,
      async handler() {
        if (
          (this.alertData.configurationType === 'persistence' &&
            (this.alertData.persistenceFrom > this.alertData.persistenceTo ||
              this.alertData.persistenceTo < this.alertData.persistenceFrom)) ||
          (this.alertData.configurationType === 'aggregateDays' &&
            (!Number.isInteger(this.alertData.aggregateDays) ||
              this.alertData.aggregateDays < ADVANCED_ACMF_AGGREGATE_DAYS_MIN ||
              this.alertData.aggregateDays >
                ADVANCED_ACMF_AGGREGATE_DAYS_MAX)) ||
          (this.alertData.configurationType === 'aggregateReports' &&
            (!Number.isInteger(this.alertData.aggregateReports) ||
              this.alertData.aggregateReports <
                ADVANCED_ACMF_AGGREGATE_REPORTS_MIN ||
              this.alertData.aggregateReports >
                ADVANCED_ACMF_AGGREGATE_REPORTS_MAX))
        ) {
          this.persistenceInvalid = true
          this.$emit('invalidPersistence')
        } else {
          this.persistenceInvalid = false
          this.$emit('validPersistence')
        }
      },
    },
  },
  beforeDestroy() {
    // Revoke blob URL to free memory
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl)
    }
  },

  methods: {
    handleDatabricksErrorNotificationClose() {
      this.$emit('onDatabricksErrorNotificationClose')
    },
    handleIsDirty() {
      this.$emit('isDirty')
    },
    handleUpdatedText(expression) {
      this.disableSave = expression.replace(/\s/g, '') === this.emptyComment
      this.updatedEditorContent = expression
      this.$emit('handleTextUpdated', expression)
    },
    handleParameterValidator(value, text) {
      this.isValidParameters = value
      let parentLogicExpression = this.$options.parent.alertData?.expression
      if (parentLogicExpression?.indexOf(':') > 0) {
        parentLogicExpression =
          this.$options.parent.alertData.expression.replace(/:\d+\]/g, ']')
      }
      if (parentLogicExpression && parentLogicExpression !== text) {
        this.$emit('isSaveDisabled', false)
      }
    },
    handleValidate() {
      if (!this.logicStringValid) {
        this.$emit('handleLogicValidate', this.updatedEditorContent)
      }
    },
    handlePerformanceAnanlysis(date) {
      this.$emit('onPerformanceAnanlysis', date)
    },
    handleCancelPerformanceAnalysis() {
      this.$emit('onCancelPerformanceAnalysis')
    },
    handleSeeEstimateDash() {
      this.$emit('onSeeEstimateDash')
    },
  },
}
</script>

<style lang="scss">
.asc-advanced-custom-alerts-expression-wrapper {
  .configuration-container {
    border: 1px solid $gray-300;
  }
  .expression-input {
    .col {
      & > label {
        font-size: 12px;
        color: $gray-700;
      }
    }

    .inline-input {
      input {
        text-align: center;
        width: 50px;
      }
    }
    .configuration {
      .aircraftSelector,
      .partnumberSelector {
        width: 50%;
        padding: 2px;
      }
    }

    .logic-string-validation-error {
      margin-bottom: 20px;
    }

    .asc-accordion {
      z-index: 1;

      .asc-pdf-viewer-link {
        a {
          color: $accent-700 !important;
          text-decoration: none;
          outline: none !important;

          &:hover {
            text-decoration: underline;
          }
        }

        .v-icon {
          color: $accent-700 !important;
        }
      }

      .logic-string-builder-wrapper {
        display: flex;
        border-top: 1px solid $gray-500;
        padding-top: 1rem;
      }

      .logic-validation-wrapper {
        margin-top: 15px;
        border-top: 1px solid $gray-500;

        .asc-accent-button-container {
          margin-top: 15px;
          float: right;

          button {
            width: 80px;
          }
        }
      }
    }
  }
}
</style>
